/**
 * SoundWidgetRex - Widget de sonido usando Rex UI
 *
 * Un widget elegante para controlar volumen de música y SFX
 * con sliders nativos de Rex UI y animaciones suaves.
 */

import { Scene, GameObjects } from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { audioManager } from '../core/CoreManagerAudio';
import { t } from '../i18n/Localization';

export interface SoundWidgetRexConfig {
  scene: Scene;
  x: number;
  y: number;
  scale?: number;
  openDirection?: 'left' | 'right';
  onMusicChange?: (volume: number) => void;
  onSfxChange?: (volume: number) => void;
}

export class SoundWidgetRex extends GameObjects.Container {
  private rexUI: UIPlugin;
  private iconButton: GameObjects.Image;
  private panel: any; // Rex UI Sizer
  private isPanelOpen: boolean = false;
  private musicVolume: number = 1;
  private sfxVolume: number = 1;
  private prevMusicVolume: number = 1;
  private prevSfxVolume: number = 1;
  private onMusicChange: ((volume: number) => void) | undefined;
  private onSfxChange: ((volume: number) => void) | undefined;
  private musicValueText: GameObjects.Text | null = null;
  private sfxValueText: GameObjects.Text | null = null;
  private pointerDownHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;

  private static readonly PANEL_WIDTH = 260;
  private static readonly PANEL_HEIGHT = 120;

  constructor(config: SoundWidgetRexConfig) {
    super(config.scene, config.x, config.y);

    // Get Rex UI plugin
    this.rexUI = (config.scene as any).rexUI as UIPlugin;

    const iconScale = config.scale ?? 0.6;
    this.onMusicChange = config.onMusicChange;
    this.onSfxChange = config.onSfxChange;

    // Load saved volumes
    this.loadVolumes();

    // Sound icon button
    this.iconButton = config.scene.add.image(0, 0, this.isMuted() ? 'icon_mute' : 'icon_sound');
    this.iconButton.setScale(iconScale);
    this.iconButton.setInteractive({ useHandCursor: true });
    this.iconButton.on('pointerover', () => this.iconButton.setTint(0x00ff00));
    this.iconButton.on('pointerout', () => this.iconButton.clearTint());
    this.iconButton.on('pointerdown', () => this.togglePanel());
    this.add(this.iconButton);

    // Create panel with Rex UI
    const panelX = config.openDirection === 'right' ? 20 : -SoundWidgetRex.PANEL_WIDTH + 20;
    this.panel = this.createRexPanel(config.scene, panelX, 50);
    this.panel.setVisible(false);
    this.panel.setAlpha(0);

    // Close panel when clicking outside
    this.pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      if (this.isPanelOpen && !this.isPointerOverWidget(pointer)) {
        this.closePanel();
      }
    };
    config.scene.input.on('pointerdown', this.pointerDownHandler);

    // Listen for scene shutdown to cleanup before Phaser does
    config.scene.events.once('shutdown', this.cleanup, this);

    config.scene.add.existing(this);
  }

  private cleanup(): void {
    // Remove event listener we registered
    if (this.pointerDownHandler && this.scene?.input) {
      this.scene.input.off('pointerdown', this.pointerDownHandler);
      this.pointerDownHandler = null;
    }

    // Kill any running tweens on the panel to prevent callbacks after shutdown
    if (this.panel && this.scene?.tweens) {
      this.scene.tweens.killTweensOf(this.panel);
    }

    // Clear references - let Phaser destroy the panel naturally
    // DO NOT call panel.destroy() here as it causes double-destroy errors
    this.panel = null;
    this.musicValueText = null;
    this.sfxValueText = null;
  }

  private loadVolumes(): void {
    // Load from audioManager (single source of truth)
    this.musicVolume = audioManager.getMusicVolume();
    this.sfxVolume = audioManager.getSfxVolume();
  }

  private saveVolumes(): void {
    // Delegate to audioManager (handles localStorage)
    audioManager.setMusicVolume(this.musicVolume);
    audioManager.setSfxVolume(this.sfxVolume);
  }

  private isMuted(): boolean {
    return this.musicVolume === 0 && this.sfxVolume === 0;
  }

  private createRexPanel(scene: Scene, x: number, y: number): any {
    const COLOR_PRIMARY = 0x1a2a3a;
    const COLOR_LIGHT = 0x4a9c6d;
    const COLOR_DARK = 0x2a3a4a;

    // Create the main sizer
    const sizer = this.rexUI.add.sizer({
      x: this.x + x + SoundWidgetRex.PANEL_WIDTH / 2,
      y: this.y + y + SoundWidgetRex.PANEL_HEIGHT / 2,
      width: SoundWidgetRex.PANEL_WIDTH,
      orientation: 'y',
      space: { left: 15, right: 15, top: 15, bottom: 15, item: 15 }
    });

    // Background
    sizer.addBackground(
      this.rexUI.add.roundRectangle(0, 0, 0, 0, 12, COLOR_PRIMARY)
        .setStrokeStyle(3, COLOR_LIGHT)
    );

    // Music toggle row
    const musicRow = this.rexUI.add.sizer({ orientation: 'x', space: { item: 10 } });
    const musicLabel = scene.add.text(0, 0, t('sound.music'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '18px',
      color: '#ffffff'
    });
    this.musicValueText = scene.add.text(0, 0, this.musicVolume === 0 ? t('ui.off') : t('ui.on'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '14px',
      color: '#4CAF50'
    });
    const musicBtn = this.createToggleButton(scene, COLOR_DARK, () => this.toggleMusic());
    musicRow.add(musicLabel, { expand: false });
    musicRow.add(musicBtn, { expand: false });
    musicRow.add(this.musicValueText, { expand: false });
    musicRow.layout();
    sizer.add(musicRow, { expand: false, align: 'center' });

    // SFX toggle row
    const sfxRow = this.rexUI.add.sizer({ orientation: 'x', space: { item: 10 } });
    const sfxLabel = scene.add.text(0, 0, t('sound.sfx'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '18px',
      color: '#ffffff'
    });
    this.sfxValueText = scene.add.text(0, 0, this.sfxVolume === 0 ? t('ui.off') : t('ui.on'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '14px',
      color: '#4CAF50'
    });
    const sfxBtn = this.createToggleButton(scene, COLOR_DARK, () => this.toggleSfx());
    sfxRow.add(sfxLabel, { expand: false });
    sfxRow.add(sfxBtn, { expand: false });
    sfxRow.add(this.sfxValueText, { expand: false });
    sfxRow.layout();
    sizer.add(sfxRow, { expand: false, align: 'center' });

    // Layout the sizer
    sizer.layout();

    return sizer;
  }

  private togglePanel(): void {
    if (this.isPanelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  private openPanel(): void {
    this.isPanelOpen = true;
    // Recargar valores por si cambiaron en otra escena
    this.loadVolumes();
    if (this.musicValueText) this.musicValueText.setText(this.musicVolume === 0 ? t('ui.off') : t('ui.on'));
    if (this.sfxValueText) this.sfxValueText.setText(this.sfxVolume === 0 ? t('ui.off') : t('ui.on'));
    this.updateIcon();
    this.panel.setVisible(true);

    // Animate open
    this.scene.tweens.add({
      targets: this.panel,
      alpha: 1,
      scaleX: { from: 0.8, to: 1 },
      scaleY: { from: 0.8, to: 1 },
      duration: 150,
      ease: 'Back.easeOut'
    });
  }

  private closePanel(): void {
    this.isPanelOpen = false;

    // Animate close
    this.scene.tweens.add({
      targets: this.panel,
      alpha: 0,
      scaleX: 0.8,
      scaleY: 0.8,
      duration: 100,
      ease: 'Power2',
      onComplete: () => {
        this.panel.setVisible(false);
      }
    });
  }

  private isPointerOverWidget(pointer: Phaser.Input.Pointer): boolean {
    // Check icon bounds
    const iconBounds = this.iconButton.getBounds();
    if (iconBounds.contains(pointer.x, pointer.y)) {
      return true;
    }

    // Check panel bounds if open
    if (this.isPanelOpen) {
      const panelBounds = this.panel.getBounds();
      if (panelBounds.contains(pointer.x, pointer.y)) {
        return true;
      }
    }

    return false;
  }

  private updateIcon(): void {
    this.iconButton.setTexture(this.isMuted() ? 'icon_mute' : 'icon_sound');
  }

  private createToggleButton(scene: Scene, bgColor: number, onClick: () => void): GameObjects.Container {
    const container = scene.add.container(0, 0);
    const bg = this.rexUI.add.roundRectangle(0, 0, 60, 24, 6, bgColor)
      .setStrokeStyle(2, 0xffffff);
    const label = scene.add.text(0, 0, t('ui.toggle'), {
      fontFamily: 'Crackvetica, Arial',
      fontSize: '10px',
      color: '#ffffff'
    }).setOrigin(0.5);
    container.add([bg, label]);
    container.setSize(60, 24);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerdown', () => onClick());
    return container;
  }

  private toggleMusic(): void {
    if (this.musicVolume > 0) {
      this.prevMusicVolume = this.musicVolume;
      this.musicVolume = 0;
    } else {
      this.musicVolume = this.prevMusicVolume || 1;
    }
    this.saveVolumes();
    if (this.musicValueText) this.musicValueText.setText(this.musicVolume === 0 ? t('ui.off') : t('ui.on'));
    this.updateIcon();
    if (this.onMusicChange) this.onMusicChange(this.musicVolume);
  }

  private toggleSfx(): void {
    if (this.sfxVolume > 0) {
      this.prevSfxVolume = this.sfxVolume;
      this.sfxVolume = 0;
    } else {
      this.sfxVolume = this.prevSfxVolume || 1;
    }
    this.saveVolumes();
    if (this.sfxValueText) this.sfxValueText.setText(this.sfxVolume === 0 ? t('ui.off') : t('ui.on'));
    this.updateIcon();
    if (this.onSfxChange) this.onSfxChange(this.sfxVolume);
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  getSfxVolume(): number {
    return this.sfxVolume;
  }

  override destroy(fromScene?: boolean): void {
    // Cleanup if not already done by shutdown event
    this.cleanup();
    super.destroy(fromScene);
  }
}
